import { useEffect, useRef } from 'react';
import { POBREZA_CRONICA_ARGENTINA_LAYER_STYLE } from '../styles/mapTheme';
import {
  getSignedPmtilesUrl,
  refreshPmtilesVectorSourceIfNeeded,
} from '../../Utils/pmtilesAuth';
import { waitForMapStyleReady } from '../utils/waitForMapStyleReady';

const TILES_BASE = 'https://s3.us-east-1.amazonaws.com/argenlot.lots';

const SOURCE_CONFIGS = [
  {
    sourceId: 'pobreza-cronica-prov',
    layerId: 'pobreza-cronica-prov-fill',
    sourceLayer: 'pobreza_cronica_prov',
    originalUrl:
      `pmtiles://${TILES_BASE}/tiles/reference/censo_2010_pobreza_cronica_argentina_prov_v1.pmtiles`,
    minzoom: 4,
    maxzoom: 6,
  },
  {
    sourceId: 'pobreza-cronica-dept',
    layerId: 'pobreza-cronica-dept-fill',
    sourceLayer: 'pobreza_cronica_dept',
    originalUrl:
      `pmtiles://${TILES_BASE}/tiles/reference/censo_2010_pobreza_cronica_argentina_dept_v1.pmtiles`,
    minzoom: 7,
    maxzoom: 9,
  },
  {
    sourceId: 'pobreza-cronica-radio',
    layerId: 'pobreza-cronica-radio-fill',
    sourceLayer: 'pobreza_cronica_radio',
    originalUrl:
      `pmtiles://${TILES_BASE}/tiles/reference/censo_2010_pobreza_cronica_argentina_radio_v1.pmtiles`,
    minzoom: 10,
    maxzoom: 20,
  },
];

const LAYER_IDS = SOURCE_CONFIGS.map(({ layerId }) => layerId);

const { fillOpacity, fillFallbackColor, outlineColor } = POBREZA_CRONICA_ARGENTINA_LAYER_STYLE;

const FILL_COLOR_EXPRESSION = ['coalesce', ['get', 'fill_color'], fillFallbackColor];

function getInsertBeforeLayer(map) {
  const layers = map.getStyle()?.layers ?? [];
  const parcelLayer = layers.find((layer) => layer.id.startsWith('self-hosted-vector-layer-'));
  return parcelLayer?.id ?? undefined;
}

function applyVisibility(map, visible) {
  const visibility = visible ? 'visible' : 'none';
  LAYER_IDS.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  });
}

function ensureSourceAndLayers(map, signedUrlsBySourceId, visible, opacity) {
  const visibility = visible ? 'visible' : 'none';

  SOURCE_CONFIGS.forEach((config) => {
    const {
      sourceId,
      layerId,
      sourceLayer,
      minzoom,
      maxzoom,
    } = config;

    if (!map.getSource(sourceId)) {
      const signedUrl = signedUrlsBySourceId[sourceId];
      if (!signedUrl) return;
      map.addSource(sourceId, {
        type: 'vector',
        url: signedUrl,
      });
    }

    if (!map.getLayer(layerId)) {
      map.addLayer(
        {
          id: layerId,
          type: 'fill',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom,
          maxzoom,
          layout: { visibility },
          paint: {
            'fill-color': FILL_COLOR_EXPRESSION,
            'fill-opacity': opacity,
            'fill-outline-color': outlineColor,
          },
        },
        getInsertBeforeLayer(map),
      );
    }
  });

  applyVisibility(map, visible);
}

function detachMoveEnd(map, moveEndHandlerRef) {
  const handler = moveEndHandlerRef.current;
  if (handler && map) map.off('moveend', handler);
  moveEndHandlerRef.current = null;
}

function attachMoveEnd(map, moveEndHandlerRef, enabledRef) {
  detachMoveEnd(map, moveEndHandlerRef);

  const onMoveEnd = () => {
    if (!enabledRef.current) return;

    void Promise.all(
      SOURCE_CONFIGS.map(({ sourceId, originalUrl }) =>
        refreshPmtilesVectorSourceIfNeeded(map, sourceId, originalUrl)),
    ).finally(() => {
      applyVisibility(map, enabledRef.current);
    });
  };

  moveEndHandlerRef.current = onMoveEnd;
  map.on('moveend', onMoveEnd);
}

/**
 * Gestiona la capa Pobreza Crónica Argentina (Censo 2010) sobre MapLibre.
 * Tres PMTiles con geometrías distintas: provincias (z 4–6), departamentos (7–9), radios (10–13).
 *
 * @param {object}                 options
 * @param {React.MutableRefObject} options.mapInstanceRef  Ref al mapa MapLibre.
 * @param {boolean}                [options.enabled=false] Mostrar la capa.
 * @param {number}                 [options.opacity]       Opacidad de relleno.
 */
export function usePobrezaCronicaArgentinaLayer({
  mapInstanceRef,
  enabled = false,
  opacity = fillOpacity,
  mapAvailTick = 0,
}) {
  const enabledRef = useRef(enabled);
  const layersReadyRef = useRef(false);
  const moveEndHandlerRef = useRef(null);
  const setupGenRef = useRef(0);

  enabledRef.current = enabled;

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !layersReadyRef.current) return;
    applyVisibility(map, enabled);
    if (!enabled) detachMoveEnd(map, moveEndHandlerRef);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    if (!enabled) {
      applyVisibility(map, false);
      detachMoveEnd(map, moveEndHandlerRef);
      layersReadyRef.current = false;
      return undefined;
    }

    const setupGen = setupGenRef.current + 1;
    setupGenRef.current = setupGen;
    let cancelled = false;

    const setup = async () => {
      await waitForMapStyleReady(map);
      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;

      const signedUrlsBySourceId = Object.fromEntries(
        await Promise.all(
          SOURCE_CONFIGS.map(async ({ sourceId, originalUrl }) => (
            [sourceId, await getSignedPmtilesUrl(originalUrl)]
          )),
        ),
      );

      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;

      const hasAnySignedUrl = SOURCE_CONFIGS.some(({ sourceId }) => signedUrlsBySourceId[sourceId]);
      if (!hasAnySignedUrl) return;

      ensureSourceAndLayers(map, signedUrlsBySourceId, true, opacity);
      layersReadyRef.current = true;
      applyVisibility(map, true);
      attachMoveEnd(map, moveEndHandlerRef, enabledRef);
    };

    void setup();

    return () => {
      cancelled = true;
      detachMoveEnd(map, moveEndHandlerRef);
    };
  }, [enabled, mapInstanceRef, opacity, mapAvailTick]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !layersReadyRef.current) return;

    LAYER_IDS.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'fill-opacity', opacity);
      }
    });
  }, [opacity, mapInstanceRef]);
}
